import { useRef, useState } from "react";
import { Canvas, type ThreeEvent } from "@react-three/fiber";
import {
  GizmoHelper,
  GizmoViewport,
  Grid,
  OrbitControls,
  PivotControls,
} from "@react-three/drei";
import * as THREE from "three";

import type { Cabinet, Id, Obstacle, Project, Wall } from "@/lib/types";
import { expandWallDecorations, expandWallPieces, snapToWall, type WallDecoration, type WallPiece } from "@/lib/geometry";
import { OBSTACLE_COLORS } from "@/panels/RoomEditorPanel";
import { CabinetMesh } from "./CabinetMesh";
import { RoomPanorama } from "./RoomPanorama";

interface Props {
  project: Project | null;
  cabinets: Cabinet[];
  selectedIds: Set<Id>;
  collisions: Set<Id>;
  outOfBounds: Set<Id>;
  swingBlocks: Set<Id>;
  onSelect: (id: Id | null, shift?: boolean) => void;
  /** Persist new position/rotation. Called on drag-end only (no per-frame). */
  onMove: (id: Id, changes: Partial<Cabinet>) => void;
  /** PBR mode: HDRI environment + physical materials. */
  pbr: boolean;
}

/**
 * RoomScene — Phase 6.
 *
 *   - Renders walls + cabinets from the project.
 *   - Selected cabinet wears a <PivotControls> gizmo for drag + rotate.
 *   - Empty floor click deselects.
 *   - PBR toggle swaps fixed lights for an HDRI environment preset.
 */
export function RoomScene({ project, cabinets, selectedIds, collisions, outOfBounds, swingBlocks, onSelect, onMove, pbr }: Props) {
  const walls = project?.room?.walls?.length ? project.room.walls : defaultRoom();
  const obstacles = project?.room?.obstacles ?? [];
  const panorama = project?.room?.panorama ?? null;
  const ceilingHeight = project?.room?.height ?? 96;
  // When a panorama backdrops the scene, drop wall fill opacity so the
  // photographed room shows through behind cabinets / snap guides.
  const wallOpacityBoost = panorama ? 0.35 : 1;
  // PivotControls only shows on single-cabinet selection; multi-selection
  // gets outline-only.
  const soleSelected = selectedIds.size === 1
    ? cabinets.find((c) => c._id === [...selectedIds][0]) ?? null
    : null;
  const unselected = cabinets.filter((c) => !selectedIds.has(c._id) || c._id !== soleSelected?._id);

  return (
    <div className="scene-container">
      <Canvas
        camera={{ position: [260, 180, 260], fov: 45, near: 1, far: 5000 }}
        shadows
      >
        {pbr ? (
          <>
            {/* Three-point lighting + warm tone for the "photoreal" look.
                Full PBR with HDRI envmap is Phase 6.1 (needs bundled .hdr). */}
            <ambientLight intensity={0.3} color="#fff4e0" />
            <directionalLight position={[150, 250, 150]} intensity={1.4} castShadow color="#fff4e0" shadow-mapSize-width={2048} shadow-mapSize-height={2048} />
            <directionalLight position={[-100, 200, -120]} intensity={0.5} color="#a0c8ff" />
            <hemisphereLight args={["#fff4e0", "#202b3a", 0.4]} />
          </>
        ) : (
          <>
            <ambientLight intensity={0.5} />
            <directionalLight position={[150, 250, 150]} intensity={1.2} castShadow shadow-mapSize-width={2048} shadow-mapSize-height={2048} />
          </>
        )}

        {/* Inch-major grid: minor 6", major 12" (1ft). */}
        <Grid
          args={[600, 600]}
          cellSize={6}
          cellThickness={0.5}
          cellColor="#3a4555"
          sectionSize={12}
          sectionThickness={1}
          sectionColor="#5b6b80"
          infiniteGrid
          fadeDistance={500}
        />

        {/* Invisible floor for deselection clicks. */}
        <mesh
          position={[0, -0.01, 0]}
          rotation={[-Math.PI / 2, 0, 0]}
          onClick={(e: ThreeEvent<MouseEvent>) => { e.stopPropagation(); onSelect(null); }}
        >
          <planeGeometry args={[2000, 2000]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>

        {panorama && (
          <RoomPanorama panorama={panorama} ceilingHeight={ceilingHeight} />
        )}

        {expandWallPieces(walls).map((p, i) => (
          <WallPieceMesh key={i} piece={p} pbr={pbr} opacityScale={wallOpacityBoost} />
        ))}
        {expandWallDecorations(walls).map((d, i) => (
          <WallDecorationMesh key={i} dec={d} pbr={pbr} />
        ))}
        {obstacles.map((o, i) => (
          <ObstacleMesh key={"obs" + i} obstacle={o} pbr={pbr} />
        ))}

        {unselected.map((c) => (
          <CabinetMesh
            key={c._id}
            cabinet={c}
            selected={selectedIds.has(c._id)}
            collision={collisions.has(c._id)}
            outOfBounds={outOfBounds.has(c._id)}
            swingBlock={swingBlocks.has(c._id)}
            onSelect={onSelect}
          />
        ))}

        {soleSelected && (
          <SelectedCabinet
            cabinet={soleSelected}
            walls={walls}
            collision={collisions.has(soleSelected._id)}
            outOfBounds={outOfBounds.has(soleSelected._id)}
            swingBlock={swingBlocks.has(soleSelected._id)}
            onSelect={onSelect}
            onMove={(changes) => onMove(soleSelected._id, changes)}
          />
        )}

        <OrbitControls makeDefault enableDamping target={[72, 40, 54]} />
        <GizmoHelper alignment="bottom-right" margin={[60, 60]}>
          <GizmoViewport labelColor="white" axisHeadScale={1} />
        </GizmoHelper>
      </Canvas>
    </div>
  );
}

// ── Selected cabinet with drag gizmo ─────────────────────────────

interface SelectedProps {
  cabinet: Cabinet;
  walls: Wall[];
  collision: boolean;
  outOfBounds: boolean;
  swingBlock: boolean;
  onSelect: (id: Id | null, shift?: boolean) => void;
  onMove: (changes: Partial<Cabinet>) => void;
}

function SelectedCabinet({ cabinet, walls, collision, outOfBounds, swingBlock, onSelect, onMove }: SelectedProps) {
  // Matrix4 instance owned by us; PivotControls reads + writes it during drag.
  const matrix = useRef(new THREE.Matrix4());
  // Bump on commit to force a fresh <PivotControls /> mount with identity.
  const [version, setVersion] = useState(0);

  function handleDragEnd() {
    const m = matrix.current;
    const pos   = new THREE.Vector3();
    const quat  = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    m.decompose(pos, quat, scale);
    const euler = new THREE.Euler().setFromQuaternion(quat, "YXZ");

    const dx = pos.x, dy = pos.y, dz = pos.z;
    const drotY = euler.y;

    // No-op if drag delta is negligible (a click that didn't actually drag).
    if (Math.abs(dx) + Math.abs(dy) + Math.abs(dz) + Math.abs(drotY) < 0.01) {
      matrix.current.identity();
      setVersion((v) => v + 1);
      return;
    }

    // Floor-anchored cabinets ignore Y movement (keep on floor).
    const newY = cabinet.anchor === "wall"
      ? Math.max(24, cabinet.position[1] + dy)
      : 0;

    let newPos: [number, number, number] = [
      cabinet.position[0] + dx,
      newY,
      cabinet.position[2] + dz,
    ];
    let newRot = cabinet.rotation + drotY;

    // Snap-to-wall: nudges position + rotation if the cabinet's back face is
    // within SNAP_DIST of any axis-aligned wall and rotation is close to the
    // perpendicular angle for that wall.
    const snap = snapToWall(
      newPos,
      newRot,
      { w: cabinet.width, h: cabinet.height, d: cabinet.depth },
      walls,
    );
    if (snap) {
      newPos = snap.position;
      newRot = snap.rotation;
    }

    onMove({ position: newPos, rotation: newRot });

    matrix.current.identity();
    setVersion((v) => v + 1);
  }

  // Anchor the gizmo at the cabinet's bottom-back-left corner (its local
  // origin) so handles sit at a natural spot.
  return (
    <PivotControls
      key={version}
      matrix={matrix.current}
      anchor={[-0.5, -0.5, -0.5]}
      depthTest={false}
      lineWidth={2}
      scale={60}
      fixed
      disableScaling
      onDrag={(local) => { matrix.current.copy(local); }}
      onDragEnd={handleDragEnd}
    >
      <CabinetMesh
        cabinet={cabinet}
        selected
        collision={collision}
        outOfBounds={outOfBounds}
        swingBlock={swingBlock}
        onSelect={onSelect}
      />
    </PivotControls>
  );
}

// ── Walls ────────────────────────────────────────────────────────

function WallPieceMesh({ piece, pbr, opacityScale = 1 }: { piece: WallPiece; pbr: boolean; opacityScale?: number }) {
  if (piece.length < 1e-3 || piece.height < 1e-3) return null;
  // header/sill use a slightly different tone so the openings read clearly.
  const baseColor = piece.kind === "solid" ? "#5b6b80" : "#6b7585";
  return (
    <mesh
      position={[piece.cx, piece.yMin + piece.height / 2, piece.cz]}
      rotation={[0, -piece.rotation, 0]}
      castShadow
      receiveShadow
    >
      <boxGeometry args={[piece.length, piece.height, piece.thickness]} />
      <meshStandardMaterial
        color={baseColor}
        roughness={pbr ? 0.7 : 0.95}
        metalness={pbr ? 0.05 : 0}
        transparent opacity={0.6 * opacityScale}
      />
    </mesh>
  );
}

function WallDecorationMesh({ dec, pbr }: { dec: WallDecoration; pbr: boolean }) {
  if (dec.length < 0.3 || dec.height < 0.3) return null;
  const isGlass  = dec.kind === "glass";
  const isHandle = dec.kind === "door_handle";

  const color =
    isGlass             ? "#9ec0e8" :
    dec.kind === "door_leaf"   ? "#5e3b1f" :
    dec.kind === "mullion"     ? "#dec3a4" :
    /* door_handle */            "#b0a060";

  return (
    <mesh
      position={[dec.cx, dec.cy, dec.cz]}
      rotation={[0, -dec.rotation, 0]}
      castShadow={!isGlass}
      receiveShadow
    >
      {isHandle ? (
        <sphereGeometry args={[dec.length / 2, 12, 8]} />
      ) : (
        <boxGeometry args={[dec.length, dec.height, dec.thickness]} />
      )}
      <meshStandardMaterial
        color={color}
        roughness={isGlass ? 0.1 : isHandle ? 0.25 : 0.55}
        metalness={isGlass ? 0.4 : isHandle ? 0.85 : 0.05}
        transparent={isGlass}
        opacity={isGlass ? 0.35 : 1}
        emissive={isGlass && pbr ? "#1a2b3a" : "#000000"}
        emissiveIntensity={isGlass && pbr ? 0.2 : 0}
      />
    </mesh>
  );
}

// ── Obstacles (radiators, pipes, outlets, columns, ...) ──────────

function ObstacleMesh({ obstacle, pbr }: { obstacle: Obstacle; pbr: boolean }) {
  const color = OBSTACLE_COLORS[obstacle.kind];
  const [hx, hy, hz] = obstacle.half;
  const [cx, cy, cz] = obstacle.center;
  // Match wall rotation convention (negate around Y to flip SVG floor-plan
  // coordinate handedness into Three.js world space).
  return (
    <mesh position={[cx, cy, cz]} rotation={[0, -obstacle.yaw, 0]} castShadow receiveShadow>
      <boxGeometry args={[hx * 2, hy * 2, hz * 2]} />
      <meshStandardMaterial
        color={color}
        roughness={pbr ? 0.7 : 0.9}
        metalness={0}
        transparent
        opacity={0.55}
      />
    </mesh>
  );
}

// Backwards-compatible single-piece renderer.
export function WallMesh({ wall, pbr }: { wall: Wall; pbr: boolean }) {
  const piece: WallPiece = {
    cx: (wall.a[0] + wall.b[0]) / 2,
    cz: (wall.a[1] + wall.b[1]) / 2,
    length: Math.hypot(wall.b[0] - wall.a[0], wall.b[1] - wall.a[1]),
    thickness: wall.thickness,
    height: wall.height,
    yMin: 0,
    rotation: Math.atan2(wall.b[1] - wall.a[1], wall.b[0] - wall.a[0]),
    parentIndex: 0,
    kind: "solid",
  };
  return <WallPieceMesh piece={piece} pbr={pbr} />;
}


/** Default 144" × 108" rectangle for projects without walls. */
function defaultRoom(): Wall[] {
  const W = 144, D = 108, H = 96, T = 4;
  return [
    { a: [0, 0],   b: [W, 0],   height: H, thickness: T },
    { a: [W, 0],   b: [W, D],   height: H, thickness: T },
    { a: [W, D],   b: [0, D],   height: H, thickness: T },
    { a: [0, D],   b: [0, 0],   height: H, thickness: T },
  ];
}
